import React, { useMemo, useState, useCallback, useEffect } from "react";

import { usePermissions } from "../../Context/permissionContext";
import { useClient } from "../../hooks/ClientProvider";
import Api from "../../services/api";
import { formatPrice } from "../../utils/currency";
import { notifyError, notifySucces } from "../../utils/notifications";
import Can from "../Can";
import { ConfirmationModal } from "../ConfirmationModal";
import ModalBlockCard from "../creditCard/modals/BlockModal";
import ModalUnBlockCard from "../creditCard/modals/UnBlockModal";
import * as S from "./style";
import { formatLastFourCardNumber } from "../../utils/formatter";

const PERMISSION_TO_BLOCK_CARD = "block";
const PERMISSION_TO_CONFIRM_TRANSITION = "vipagem_confirmar_transicao";

export const CreditCardV2 = ({
  card,
  reloadCards,
  bloqCard,
  unBloqCard,
  isDependent,
  isHistoryCard,
  isTransitionCard,
  onConfirmTransition,
}) => {
  const [isModalBlockCardVisible, setIsModalBlockCardVisible] = useState(false);
  const [blockTypes, setBlockTypes] = useState([]);
  const [isUnBlockModalVisible, setIsUnBlockModalVisible] = useState(false);
  const [limit, setLimit] = useState("");
  const [lastFourToReactivate, setLastFourToReactivate] = useState(undefined);
  const [isReactivating, setIsReactivating] = useState();

  const { client } = useClient();
  const { permissions } = usePermissions();

  const clientID = client?.client?.id;
  const codProduct = client?.product?.codigoProduto;
  const isProdutoBandeirado = client?.client?.isProdutoBandeirado;
  
  const validityDate = card && card.dataValidade ? card.dataValidade.split("-") : [];

  const blockedStatuses = [
    "B",
    "BLOQUEADO",
    "BLOQUEADO_TEMPORARIO",
    "BLOQUEADO_PERMANENTE",
    "PENDENTE_TROCA",
    "CARTAO_TROCA",
    "PENDENTE_CONFIRMACAO_TROCA",
  ];

  const cardExchangeStatuses = [
    "CARTAO_TROCA",
    "PENDENTE_TROCA",
    "PENDENTE_CONFIRMACAO_TROCA",
  ];

  const unlockableStatuses = ["BLOQUEADO", "B", "BLOQUEADO_TEMPORARIO"];

  const formattedCardNumber = useMemo(() => {
    return formatLastFourCardNumber(card.numeroMascarado);
  }, [card.numeroMascarado]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const setNewDetailBlockCard = () => {};

  const getBlockTypes = useCallback(async () => {
    try {
      const resp = await Api.get("card_block_type", { isProdutoBandeirado });
      setBlockTypes(resp.data);
    } catch (error) {
      notifyError(error.message);
    }
  }, [isProdutoBandeirado]);

  const confirmExchange = useCallback(async () => {
    try {
      await Api.post("card_confirm_exchange", {
        card_id: card.id,
        product_id: codProduct,
      });
      notifySucces("Troca realizada com sucesso.");
    } catch (error) {
      notifyError(error.message);
    } finally {
      reloadCards();
    }
  }, [card.id, codProduct, reloadCards]);

  const getLimit = useCallback(async () => {
    try {
      const response = await Api.get("client_information", { client_id: clientID });
      setLimit(response.data.limitePrincipal);
    } catch (erro) {
      console.error("Falha ao buscar limite", erro);
    }
  }, [clientID]);

  const toggleModalBlockCard = useCallback(() => {
    if (permissions.includes(PERMISSION_TO_BLOCK_CARD)) {
      setIsModalBlockCardVisible((prev) => !prev);
    }
  }, [permissions]);

  const toggleModalUnBlock = useCallback(() => {
    setIsUnBlockModalVisible((prev) => !prev);
  }, []);

  const onClickToReactivateCard = useCallback(() => {
    const lastFour = formattedCardNumber ? formattedCardNumber.slice(-4) : "****";
    setLastFourToReactivate(lastFour);
  }, [formattedCardNumber]);

  const onCloseReactivateCardModal = useCallback(() => {
    setLastFourToReactivate(undefined);
  }, []);

  const reactivateCard = useCallback(async () => {
    if (isReactivating) return;
    
    try {
      setIsReactivating(true);
      await Api.patch("card_canceled_unblock", {
        client_id: clientID,
        card_id: card.id,
        cod_product: card.idProduto,
        masked_number: card.panMascarado,
        action: "Reativado",
      });
      notifySucces("Cartão reativado com sucesso.");
    } catch (error) {
      notifyError(error.message);
    } finally {
      setIsReactivating(false);
      reloadCards();
    }
  }, [card, clientID, isReactivating, reloadCards]);

  useEffect(() => {
    if (clientID) {
      getLimit();
      getBlockTypes();
    }
  }, [clientID, getLimit, getBlockTypes]);

  const canConfirmTransition = permissions?.includes(
    PERMISSION_TO_CONFIRM_TRANSITION
  );

  const renderActionButtons = () => {
    if (isTransitionCard) {
      return (
        <S.CreditCardButton
          type="button"
          onClick={onConfirmTransition}
          disabled={!canConfirmTransition}
          title={
            canConfirmTransition
              ? undefined
              : "Sem permissão para confirmar transição"
          }
        >
          Confirmar Transição
        </S.CreditCardButton>
      );
    }

    if (cardExchangeStatuses.includes(card.status)) {
      return (
        <S.CreditCardButton type="button" onClick={confirmExchange}>
          Confirmar Troca
        </S.CreditCardButton>
      );
    }

    if (isHistoryCard) {
      return null;
    }

    if (!blockedStatuses.includes(card.status)) {
      return (
        <Can permission={PERMISSION_TO_BLOCK_CARD}>
          <S.CreditCardButton type="button" onClick={toggleModalBlockCard}>
            Bloquear
          </S.CreditCardButton>
        </Can>
      );
    }

    if (unlockableStatuses.includes(card.status)) {
      return (
        <Can permission="unblock">
          <S.CreditCardButton type="button" onClick={toggleModalUnBlock}>
            Desbloquear
          </S.CreditCardButton>
        </Can>
      );
    }

    if (["C", "CANCELADO"].includes(card.status) && card.permiteReativar) {
      return (
        <Can permission="reactivate_canceled_card">
          <S.CreditCardButton type="button" onClick={onClickToReactivateCard}>
            Reativar
          </S.CreditCardButton>
        </Can>
      );
    }

    return null;
  };

  return (
    <>
      <ModalBlockCard
        isModalVisible={isModalBlockCardVisible}
        // eslint-disable-next-line react/jsx-curly-brace-presence
        blockValue={"0"}
        blockTypes={blockTypes}
        closeModal={toggleModalBlockCard}
        clientID={clientID}
        productID={codProduct}
        card={card}
        bloqCard={bloqCard}
        setNewDetailBlockCard={setNewDetailBlockCard}
        codProduct={codProduct}
        reloadCards={reloadCards}
      />

      <ModalUnBlockCard
        isModalVisible={isUnBlockModalVisible}
        closeModal={toggleModalUnBlock}
        card={card}
        unBloqCard={unBloqCard}
        clientID={clientID}
        productID={codProduct}
        setDetailBlockCard={null}
        reloadCards={reloadCards}
      />

      {!!lastFourToReactivate && (
        <ConfirmationModal
          onClose={onCloseReactivateCardModal}
          title="Reativar cartão"
          description={`Deseja realmente reativar o cartão com final ${lastFourToReactivate}?`}
          onSubmit={reactivateCard}
          isLoading={isReactivating}
        />
      )}

      <S.CreditCardContainer>
        <S.CreditCardTop>
         <span>{formatPrice(limit)}</span>
        </S.CreditCardTop>
        
        <S.CreditCardLogo src="/images/logo-pequeno-rpe.png" alt="Logo RPE" />
        
        <S.CreditCardInfo>
          <div>
            <span className="credit-card-number">{formattedCardNumber}</span>
            <span>{card.nome}</span>
          </div>
          {!!validityDate.length && (
            <div className="validity-container">
              <span>Validade</span>
              <span className="credit-card-validity">
                {validityDate[1]}/{validityDate[0]}
              </span>
            </div>
          )}
        </S.CreditCardInfo>
        
        <S.CreditCardBottom>
          {card.statusEmbossing === "EMBOSSADO" && <span>Impresso</span>}
          <span>{card.virtual ? "Virtual" : "Físico"}</span>
            {renderActionButtons()}
        </S.CreditCardBottom>
      </S.CreditCardContainer>
    </>
  );
};